import { useState, useEffect } from 'react';
import axios from 'axios';
import { parseLocalDate } from '@/utils/dateUtils';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Gift, Users, TrendingUp, DollarSign, Clock, 
  CheckCircle, XCircle, RefreshCw, Award, Heart,
  ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';

const API_URL = window.location.origin;

const AdminGiftDashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [giftsList, setGiftsList] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchDashboard();
    fetchConversions();
  }, []);

  useEffect(() => {
    if (activeTab === 'list') {
      fetchGiftsList();
    }
  }, [activeTab, statusFilter, page]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/gifts/dashboard?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch gift dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/gifts/conversions?days=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversions(response.data);
    } catch (error) {
      console.error('Failed to fetch conversions:', error);
    }
  };

  const triggerReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/gifts/reminders/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Gift reminders sent! Free trial: ${response.data.free_trial_reminders?.reminders_sent || 0}, Paid: ${response.data.paid_gift_reminders?.reminders_sent || 0}`);
    } catch (error) {
      console.error('Failed to trigger reminders:', error);
      alert('Failed to trigger reminders');
    }
  };

  const fetchGiftsList = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/gifts/list?page=${page}&limit=15`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGiftsList(response.data);
    } catch (error) {
      console.error('Failed to fetch gifts list:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-forest" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="admin-gift-dashboard">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest flex items-center gap-3">
              <Gift className="h-8 w-8" />
              Gift Tracking Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Monitor gift memberships, conversions, and top gift senders.
            </p>
          </div>
          <Button onClick={triggerReminders} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Send Reminders Now
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand/30">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="conversions">Conversions</TabsTrigger>
            <TabsTrigger value="leaderboard">Top Senders</TabsTrigger>
            <TabsTrigger value="list">All Gifts</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {dashboard && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Gifts Sent</p>
                          <p className="text-3xl font-bold text-forest">{dashboard.summary.total_gifts}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {dashboard.summary.free_trial_gifts} trials + {dashboard.summary.paid_gifts} paid
                          </p>
                        </div>
                        <Gift className="h-10 w-10 text-forest/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Conversion Rate</p>
                          <p className="text-3xl font-bold text-terracotta">{dashboard.summary.conversion_rate}%</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {dashboard.summary.free_trial_accepted} accepted / {dashboard.summary.free_trial_gifts} sent
                          </p>
                        </div>
                        <TrendingUp className="h-10 w-10 text-terracotta/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Est. Revenue</p>
                          <p className="text-3xl font-bold text-green-600">${dashboard.summary.estimated_revenue.toLocaleString()}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            From gift conversions
                          </p>
                        </div>
                        <DollarSign className="h-10 w-10 text-green-600/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Pending Gifts</p>
                          <p className="text-3xl font-bold text-amber-600">{dashboard.summary.free_trial_pending}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Awaiting acceptance
                          </p>
                        </div>
                        <Clock className="h-10 w-10 text-amber-600/30" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Gift Status Breakdown */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg text-forest">
                        Free Trial Gift Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            <span className="text-sm">Accepted</span>
                          </div>
                          <span className="font-medium">{dashboard.summary.free_trial_accepted}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${dashboard.summary.free_trial_gifts > 0 ? (dashboard.summary.free_trial_accepted / dashboard.summary.free_trial_gifts * 100) : 0}%` }}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-sm">Pending</span>
                          </div>
                          <span className="font-medium">{dashboard.summary.free_trial_pending}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-amber-500 h-2 rounded-full" 
                            style={{ width: `${dashboard.summary.free_trial_gifts > 0 ? (dashboard.summary.free_trial_pending / dashboard.summary.free_trial_gifts * 100) : 0}%` }}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-sm">Expired</span>
                          </div>
                          <span className="font-medium">{dashboard.summary.free_trial_expired}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full" 
                            style={{ width: `${dashboard.summary.free_trial_gifts > 0 ? (dashboard.summary.free_trial_expired / dashboard.summary.free_trial_gifts * 100) : 0}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Daily Trend */}
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg text-forest">
                        Daily Gift Activity (Last 30 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.daily_trend?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <div className="flex gap-1 min-w-max">
                            {dashboard.daily_trend.slice().reverse().map((day) => (
                              <div key={day.date} className="flex flex-col items-center w-6">
                                <div className="h-16 w-full bg-gray-100 rounded-t relative overflow-hidden">
                                  <div 
                                    className="absolute bottom-0 w-full bg-forest transition-all"
                                    style={{ height: `${Math.min(day.sent * 20, 100)}%` }}
                                    title={`${day.sent} sent, ${day.accepted} accepted`}
                                  />
                                </div>
                                <p className="text-[8px] text-gray-500 mt-1 -rotate-45 origin-top-left w-8">
                                  {parseLocalDate(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No gift activity in this period</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Gifts */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-forest">
                      Recent Gifts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.recent_gifts?.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {dashboard.recent_gifts.slice(0, 10).map((gift) => (
                          <div key={gift.id} className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={gift.gifter_profile_image} />
                                <AvatarFallback className="bg-forest/20 text-forest text-xs">
                                  {gift.gifter_name?.charAt(0) || 'G'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{gift.gifter_name}</p>
                                <p className="text-xs text-gray-500">
                                  Gifted to <span className="font-medium">{gift.recipient_name}</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {getStatusBadge(gift.status)}
                              <span className="text-xs text-gray-400">{formatDate(gift.created_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No gifts sent yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Conversions Tab */}
          <TabsContent value="conversions">
            <div className="space-y-6">
              {/* Conversion Stats */}
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="card-base bg-gradient-to-br from-gold/10 to-amber-100">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-700">Total Conversions</p>
                        <p className="text-3xl font-bold text-amber-800">{conversions?.total_conversions || 0}</p>
                        <p className="text-xs text-amber-600 mt-1">Last 90 days</p>
                      </div>
                      <TrendingUp className="h-10 w-10 text-gold" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-base bg-gradient-to-br from-green-50 to-emerald-100">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-emerald-700">Estimated Value</p>
                        <p className="text-3xl font-bold text-emerald-800">${conversions?.estimated_value?.toLocaleString() || 0}</p>
                        <p className="text-xs text-emerald-600 mt-1">Based on 3-month retention</p>
                      </div>
                      <DollarSign className="h-10 w-10 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-base bg-gradient-to-br from-purple-50 to-violet-100">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-violet-700">Conversion Rate</p>
                        <p className="text-3xl font-bold text-violet-800">
                          {dashboard?.summary?.free_trial_gifts > 0 
                            ? Math.round((conversions?.total_conversions || 0) / dashboard.summary.free_trial_gifts * 100)
                            : 0}%
                        </p>
                        <p className="text-xs text-violet-600 mt-1">Of total free trial gifts</p>
                      </div>
                      <Award className="h-10 w-10 text-violet-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Conversions List */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Recent Gift Conversions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {conversions?.conversions?.length > 0 ? (
                    <div className="space-y-4">
                      {conversions.conversions.map((conv) => (
                        <div key={conv.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-4">
                            <div className="flex flex-col items-center">
                              <Avatar className="h-10 w-10 border-2 border-green-400">
                                <AvatarImage src={conv.user?.profile_image} />
                                <AvatarFallback className="bg-green-100 text-green-700 text-xs">
                                  {conv.user?.full_name?.charAt(0) || conv.user_name?.charAt(0) || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-green-600 mt-1">💎</span>
                            </div>
                            <div>
                              <p className="font-medium text-green-800">{conv.user?.full_name || conv.user_name}</p>
                              <p className="text-xs text-green-600">{conv.user?.email || conv.user_email}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Gifted by: <span className="font-medium">{conv.gifter?.full_name || conv.gifter_name}</span>
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-600 text-white">Converted!</Badge>
                            <p className="text-xs text-gray-500 mt-2">
                              {formatDate(conv.converted_at)}
                            </p>
                            <Badge variant="outline" className="mt-1">{conv.converted_tier}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No gift conversions yet.</p>
                      <p className="text-sm text-gray-400 mt-2">When gift recipients become paying members, they'll appear here!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Award className="h-5 w-5 text-gold" />
                  Top Gift Senders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.top_senders?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboard.top_senders.map((sender, index) => (
                      <div 
                        key={sender.gifter_id} 
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          index === 0 ? 'bg-gold/10 border border-gold/30' :
                          index === 1 ? 'bg-gray-100' :
                          index === 2 ? 'bg-orange-50' :
                          'bg-sand/30'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`text-2xl font-bold ${
                            index === 0 ? 'text-gold' :
                            index === 1 ? 'text-gray-400' :
                            index === 2 ? 'text-orange-400' :
                            'text-gray-500'
                          }`}>
                            #{index + 1}
                          </span>
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={sender.profile_image} />
                            <AvatarFallback className="bg-forest/20 text-forest">
                              {sender.full_name?.charAt(0) || sender.gifter_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{sender.full_name || sender.gifter_name}</p>
                            <p className="text-sm text-gray-500">{sender.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-forest">{sender.gifts_sent}</p>
                          <p className="text-xs text-gray-500">
                            {sender.gifts_accepted} accepted ({Math.round(sender.gifts_accepted / sender.gifts_sent * 100 || 0)}%)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No gift senders yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Gifts Tab */}
          <TabsContent value="list">
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg text-forest">
                    All Gifts
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant={statusFilter === null ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setStatusFilter(null); setPage(1); }}
                    >
                      All
                    </Button>
                    <Button 
                      variant={statusFilter === 'pending' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setStatusFilter('pending'); setPage(1); }}
                    >
                      Pending
                    </Button>
                    <Button 
                      variant={statusFilter === 'accepted' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setStatusFilter('accepted'); setPage(1); }}
                    >
                      Accepted
                    </Button>
                    <Button 
                      variant={statusFilter === 'expired' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setStatusFilter('expired'); setPage(1); }}
                    >
                      Expired
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {giftsList?.gifts?.length > 0 ? (
                  <>
                    <div className="space-y-3">
                      {giftsList.gifts.map((gift) => (
                        <div key={gift.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="flex flex-col items-center">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={gift.gifter?.profile_image} />
                                <AvatarFallback className="bg-forest/20 text-forest text-xs">
                                  {gift.gifter?.full_name?.charAt(0) || 'G'}
                                </AvatarFallback>
                              </Avatar>
                              <Heart className="h-4 w-4 text-pink-500 -mt-1" />
                              <Avatar className="h-10 w-10 -mt-1">
                                <AvatarImage src={gift.recipient_user?.profile_image} />
                                <AvatarFallback className="bg-terracotta/20 text-terracotta text-xs">
                                  {gift.recipient_name?.charAt(0) || 'R'}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{gift.gifter?.full_name || gift.gifter_name}</p>
                              <p className="text-xs text-gray-500">↓ gifted to</p>
                              <p className="font-medium text-sm">{gift.recipient_name}</p>
                              <p className="text-xs text-gray-400">{gift.recipient_email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(gift.status)}
                            <p className="text-xs text-gray-400 mt-2">{formatDate(gift.created_at)}</p>
                            {gift.gift_type === 'paid' && (
                              <Badge className="bg-purple-100 text-purple-700 mt-1">Paid</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    {giftsList.pagination.total_pages > 1 && (
                      <div className="flex items-center justify-center gap-4 mt-6">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-gray-600">
                          Page {page} of {giftsList.pagination.total_pages}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPage(p => Math.min(giftsList.pagination.total_pages, p + 1))}
                          disabled={page === giftsList.pagination.total_pages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-8">No gifts found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminGiftDashboardPage;
